import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// import { useApp } from "../../../../common/context/app-context";
import GiftIcon from "../../assets/icons/Gift";
import TrophyIcon from "../../assets/icons/Trophy";
import TrophyBold from "../../assets/icons/TrophyBold";
import { useHiddenChallengeRewards } from "../../common/hooks/useHiddenChallengeRewards";
import BadgeCard from "../../components/Cards/BadgeCard";
import type {
  ChallengeVariant,
} from "../../components/Cards/ChallengeCard";
import ChallengeCard from "../../components/Cards/ChallengeCard";
import Button from "../../components/Common/Button";
import Skeleton from "../../components/Common/Skeleton";
import SectionLayout from "../../components/Layouts/SectionLayout";
import Popup from "../../components/Popup";
import Slider from "../../components/Slider";
import { useGetBadgeChallengesQuery } from "../../configs/apis/GetBadgeChallenges";
import { useGetChallengesQuery } from "../../configs/apis/GetChallenges";
import type { Challenge } from "../../configs/apis/GetChallenges/models";
import { useGetProgressQuery } from "../../configs/apis/GetProgress";
import type { Prize } from "../../configs/apis/models";
import { useUpdateIsClaimedMutation } from "../../configs/apis/UpdateIsClaimed";
import { LIMIT_TO_SEE_ALL } from "../../configs/models/constants";

const DashboardPage = () => {
  const { t: translate } = useTranslation("translation");

  // const { walletSummary } = useApp();

  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [popupReward, setPopupReward] = useState<Prize | undefined>();
  const [claimableChallengeId, setClaimableChallengeId] = useState<number | undefined>();

  const [updateIsClaimed, { isLoading: isClaiming }] = useUpdateIsClaimedMutation();

  const {
    data: forYouData,
    isLoading: isForYouLoading,
    isFetching: isForYouFetching,
    isError: isForYouError,
    refetch: refetchForYou,
  } = useGetChallengesQuery(
    {
      page: 1,
      itemsPerPage: 4,
    },
    { refetchOnMountOrArgChange: true }
  );

  const {
    data: inProgressData,
    isLoading: inProgressLoading,
    isFetching: inProgressFetching,
    isError: inProgressError,
    refetch: refetchInProgress,
  } = useGetProgressQuery(
    {
      page: 1,
      itemsPerPage: 4,
    },
    { refetchOnMountOrArgChange: true }
  );

  const {
    visibleChallenges: visibleInProgress,
    queuedRewards,
    dismissFrontOfQueue,
    markChallengeClaimed,
  } = useHiddenChallengeRewards(inProgressData?.data);
  const frontOfQueue = queuedRewards[0];

  const {
    data: badgeData,
    isLoading: isBadgeLoading,
    isFetching: isBadgeFetching,
    isError: isBadgeError,
  } = useGetBadgeChallengesQuery({}, { refetchOnMountOrArgChange: true });

  const getVariant = (card: Challenge): ChallengeVariant => {
    if (card.isLocked) return "locked";
    return "active";
  };

  // Check if any initial loading is happening
  const isInitialLoading = isForYouLoading || inProgressLoading || isBadgeLoading;

  // Show loading state during initial load
  if (isInitialLoading) {
    return (
      <main className="mb-16 flex flex-col gap-16">
        <div className="flex flex-col justify-between gap-4 rounded-lg bg-activeColor px-6 py-4 sm:flex-row sm:items-center md:px-12 md:py-8">
          <div className="flex gap-2 text-variantTextColor">
            <TrophyIcon className="size-16 text-variantTextColor sm:size-24" />
            <div className="flex flex-col justify-center">
              <div className="h-6 w-48 rounded bg-variantTextColor/20 md:h-8"></div>
              <div className="mt-2 h-8 w-64 rounded bg-variantTextColor/20 md:h-10"></div>
            </div>
          </div>
          <div className="h-10 w-32 rounded bg-variantTextColor/20"></div>
        </div>
        <div className="flex flex-col gap-16">
          <div className="flex flex-col gap-4">
            <div className="h-6 w-48 rounded bg-secondTextColor/20"></div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 4 }).map((_, idx) => (
                <Skeleton key={idx} />
              ))}
            </div>
          </div>
        </div>
      </main>
    );
  }

  const isAllEmpty = (forYouData?.data?.length === 0 && inProgressData?.data?.length === 0 && badgeData?.result?.length === 0) &&
    !inProgressLoading && !isForYouLoading && !isBadgeLoading;
  
  const shouldShowInProgress = (inProgressData || inProgressFetching) &&
    (inProgressData?.count !== 0 || inProgressError || !inProgressData?.data);
  
  const shouldShowForYou = (forYouData || isForYouFetching) &&
    (forYouData?.count !== 0 || isForYouError);
  
  const renderNoChallengesPlaceholder = () => {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-3 overflow-hidden">
        <TrophyBold className="size-[200px] text-failingImageColor" />
        <p className="text-xl font-bold text-secondTextColor/70">
          {translate("no_challenges_title")}
        </p>
        <p className="text-lg text-secondTextColor/70">{translate("no_challenges_body")}</p>
      </div>
    );
  };

  const shouldRenderBadgesSection = () => {
    if (isBadgeFetching || isBadgeLoading) return false; // Don't render while fetching/loading
    if (isBadgeError || !badgeData) return false; // Don't render if there's an error or no data
    if (badgeData?.statusCode !== 1) return false; // Don't render if statusCode is not 1
    if (!badgeData?.result || badgeData?.result?.length === 0) return false; // Don't render if result is empty
    return true; // Render only if all conditions are met
  };

  const renderWelcomeHeader = () => {
    return (
      <div className="flex flex-col justify-between gap-4 rounded-lg bg-activeColor px-6 py-4 sm:flex-row sm:items-center md:px-12 md:py-8">
        <div className="flex gap-2 text-variantTextColor">
          <TrophyIcon className="size-16 text-variantTextColor sm:size-24" />
          <div className="flex flex-col justify-center">
            <p className="md:text-2xl">
              {translate("hello", { 0: "" })}
            </p>
            <p className="text-xl md:text-4xl">
              {translate("welcome_to_challenges")}
            </p>
          </div>
        </div>
        <Button
          as={Link}
          className="flex !w-fit items-center gap-2 !bg-mainColor px-4 !text-activeColor"
          to="/gamification/history"
        >
          <GiftIcon />
          <span>{translate("rewards_history")}</span>
        </Button>
      </div>
    );
  };

  return (
    <>
      <Popup
        isVisible={isPopupVisible}
        onClose={() => {
          setIsPopupVisible(false);
          setPopupReward(undefined);

          if (claimableChallengeId) {
            updateIsClaimed({
              challengeId: claimableChallengeId,
            })
              .unwrap()
              .then(() => {
                refetchForYou();
                refetchInProgress();
              })
              .catch(() => {})
              .finally(() => {
                setClaimableChallengeId(undefined);
              });
          }
        }}
        prize={popupReward}
      />
      <Popup
        isVisible={queuedRewards.length > 0 && !isPopupVisible}
        onClose={() => {
          const closingChallengeId = frontOfQueue?.challengeId;
          dismissFrontOfQueue();

          if (closingChallengeId) {
            updateIsClaimed({
              challengeId: closingChallengeId,
            })
              .unwrap()
              .then(() => {
                markChallengeClaimed(closingChallengeId);
                refetchForYou();
                refetchInProgress();
              })
              .catch(() => {});
          }
        }}
        prize={frontOfQueue?.prize}
      />
      <main className="mb-16 flex flex-col gap-16">
        {renderWelcomeHeader()}
        {isAllEmpty && renderNoChallengesPlaceholder()}
        {!isAllEmpty &&        <>
          {shouldShowInProgress && (
            <Slider
              claimingChallengeId={claimableChallengeId}
              content={visibleInProgress}
              count={
                inProgressLoading || inProgressFetching
                  ? 0
                  : inProgressData?.count ?? 0
              }
              isClaiming={isClaiming}
              isLoading={inProgressFetching}
              onClaim={(challengeId, reward) => {
                // Unreachable via Slider's getVariant; guards against an empty popup that
                // would still consume the reward on close.
                if (!reward) return;

                setClaimableChallengeId(challengeId);
                setPopupReward(reward);
                setIsPopupVisible(true);
              }}
            />
          )}
          {shouldShowForYou && (
            <SectionLayout
              count={forYouData?.count}
              hasSeeAll={
                !isForYouFetching &&
                    !!forYouData?.data &&
                    forYouData.count > LIMIT_TO_SEE_ALL
              }
              title={translate("challenges_for_you")}
              to="/gamification/all-challenges"
            >
              {isForYouLoading || isForYouFetching
                ? Array.from({ length: 3 }).map((_, idx) => (
                  <Skeleton key={idx} />
                ))
                : forYouData?.data?.map((card: Challenge) => {
                  return (
                    <ChallengeCard
                      key={card.id}
                      {...card}
                      categoryName={card?.category?.name}
                      variant={getVariant(card)}
                    />
                  );
                })}
            </SectionLayout>
          )}
          {shouldRenderBadgesSection() && (
            <SectionLayout
              hasSeeAll={false}
              title={translate("badges")}
              to="/gamification/all-challenges"
            >
              {isBadgeLoading || isBadgeFetching
                ? Array.from({ length: 3 }).map((_, idx) => (
                  <Skeleton key={idx} />
                ))
                : badgeData?.result?.map((badge) => (
                  <BadgeCard
                    key={badge.id}
                    {...badge}
                    isAcheived={!badge.isAchieved}
                    name={badge.name}
                  />
                ))}
            </SectionLayout>
          )}
        </>}
      </main>
    </>
  );
};

export default DashboardPage;